import {
  Box,
  Button,
  Divider,
  Fab,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Slide,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import React, { FC, useContext, useEffect, useState } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import CheckIcon from '@mui/icons-material/Check'
import DeleteIcon from '@mui/icons-material/Delete'
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore'
import {
  DoorPropsType,
  DoorType,
  DoorVisualType,
  DrawerPropsType,
  DrawerSlideType,
  DrawerVisualType,
  HandlePositionTypes,
  HandlePositionXTypes,
  HandlePropsType,
  newDoorVisualTypeValues,
} from '../../redux/cabinetReducer'
import LeftDoorSVG from '../../assets/leftDoor.svg?react'
import RightDoorSVG from '../../assets/rightDoor.svg?react'
import CenterHandleSVG from '../../assets/centerHandle.svg?react'
import HorizontalHandleSVG from '../../assets/horizontalHandle.svg?react'
import LiftUpDoorSVG from '../../assets/liftUpDoor.svg?react'
import DoubleDoorSVG from '../../assets/doubleDoor.svg?react'
import TopHandleSVG from '../../assets/topHandle.svg?react'
import BottomHandleSVG from '../../assets/bottomHandle.svg?react'
import FromGroundSVG from '../../assets/fromGround.svg?react'
import { handleFocus } from '../../common/helpers'
import { useSnackbar } from 'notistack'
import { Flex, showMessage } from '../common/Helpers'
import { ConfigsContext } from '../../common/configContext'
import useUnit from '../../hooks/useUnit'
import { cmToIn, inToCm } from '../common/UnitComponents'
import FivePiecesDoorSVG from '../../assets/fivePiecesDoor.svg?react'
import ShakerDoorSVG from '../../assets/shakerDoor.svg?react'
import NewDoorSVG from '../../assets/newDoor.svg?react'
import VGrooveDoorSVG from '../../assets/vGrooveDoor.svg?react'
import DrawerSVG from '../../assets/drawer.svg?react'
import DrawerNewSVG from '../../assets/drawerNew.svg?react'
import DrawerDoubleSVG from '../../assets/drawerDouble.svg?react'
import DrawerLeftSVG from '../../assets/drawerLeft.svg?react'
import DrawerRightSVG from '../../assets/drawerRight.svg?react'
import DrawerCenterSVG from '../../assets/drawerCenter.svg?react'
import {
  getNewDoorPiecesFromSplitters,
  newDoorConfigs,
} from '../../classes/3DObjects'
import { HandleOrientationType } from '../../classes/Item'
import zIndex from '@mui/material/styles/zIndex'
import { minDepthOfCabByDrawerSlideType } from '../../config'

interface ITypeButtonProps {
  selectedType: string
  type: string
  children: JSX.Element

  setType(type: string): void
}

const TypeButton: FC<ITypeButtonProps> = ({
  selectedType,
  type,
  children,
  setType,
}) => {
  const selected =
    type === '#'
      ? selectedType.startsWith('#')
      : type === 'shaker'
        ? selectedType.startsWith('shaker')
        : selectedType === type

  return (
    <Box
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        border: `${selected ? 2 : 1}px solid ${selected ? '#3f51b5' : 'black'}`,
        borderRadius: 8,
        height: 30,
        width: 30,
        cursor: 'pointer',
      }}
      onClick={() => {
        setType(type)
      }}
    >
      <Box
        style={{
          height: 26,
          width: 26,
        }}
      >
        {React.cloneElement(children, {
          width: '100%',
          height: '100%',
          viewBox: '0 0 100 100',
        })}
      </Box>
    </Box>
  )
}

interface ISplitterInputProps {
  sectionNumber: number | undefined
  normalMapSplitter: string | undefined
  panelHeight: number
  legHeight: number
  cabinetHeight: number
  sectionY: number
  newDoorsDistY: string
  setSectionDoorSplitters(value: string): void
}

const SplitterInput: FC<ISplitterInputProps> = (props) => {
  const { unit } = useUnit()
  const [splitterMode, setSplitterMode] = useState<'manual' | 'fromGround'>(
    'manual'
  )
  const [fromGroundSplitterValue, setFromGroundSplitterValue] = useState(111.96)
  const [localManualValue, setLocalManualValue] = useState('')

  useEffect(() => {
    let value = props.normalMapSplitter || ''
    if (unit === 'in') {
      const numbers = value.split(',')
      const numbersInInch = []
      for (const num of numbers) {
        const n = parseFloat(num)
        if (isNaN(n)) {
          const letter = num[0]
          const _num = num.slice(1)
          const n = parseFloat(_num)
          if (isNaN(n)) {
            numbersInInch.push(num)
          } else {
            numbersInInch.push(letter + cmToIn(n))
          }
        } else {
          numbersInInch.push(cmToIn(n))
        }
      }
      value = numbersInInch.join(',')
    }
    setLocalManualValue(value)
  }, [props.normalMapSplitter, unit])

  const applyManualValue = () => {
    let value = localManualValue
    if (unit === 'in') {
      const numbers = value.split(',')
      const numbersInInch = []
      for (const num of numbers) {
        const n = parseFloat(num)
        if (isNaN(n)) {
          const letter = num[0]
          const _num = num.slice(1)
          const n = parseFloat(_num)
          if (isNaN(n)) {
            numbersInInch.push(num)
          } else {
            numbersInInch.push(letter + inToCm(n))
          }
        } else {
          numbersInInch.push(inToCm(n))
        }
      }
      value = numbersInInch.join(',')
    }
    props.setSectionDoorSplitters(value)
    if (value === '') {
      setFromGroundSplitterValue(111.96)
    } else {
      const parts = value.split(',')
      if (parts.length === 2) {
        const bottom = parseFloat(parts[0])
        if (!isNaN(bottom)) {
          const sectionBottomFromGround =
            props.legHeight +
            props.cabinetHeight / 2 +
            props.sectionY -
            props.panelHeight / 2
          setFromGroundSplitterValue(
            parseFloat((sectionBottomFromGround + bottom).toFixed(2))
          )
        }
      }
    }
  }

  useEffect(() => {
    const existing = props.normalMapSplitter || ''
    const match = existing.match(
      /^[gG]([0-9.]+)(?:_([a-zA-Z]))?(?:_([a-zA-Z]))?$/
    )
    if (match) {
      setSplitterMode('fromGround')
      const val = parseFloat(match[1])
      setFromGroundSplitterValue(isNaN(val) ? 111.96 : val)
    } else {
      setSplitterMode('manual')
      const parts = existing.split(',')
      if (parts.length === 2) {
        const bottom = parseFloat(parts[0])
        if (!isNaN(bottom)) {
          const sectionBottomFromGround =
            props.legHeight +
            props.cabinetHeight / 2 +
            props.sectionY -
            props.panelHeight / 2
          setFromGroundSplitterValue(
            parseFloat((sectionBottomFromGround + bottom).toFixed(2))
          )
        }
      } else {
        setFromGroundSplitterValue(111.96)
      }
    }
  }, [props.sectionNumber])

  let resolvedSplitter = props.normalMapSplitter || ''
  const match = resolvedSplitter.match(
    /^[gG]([0-9.]+)(?:_([a-zA-Z]))?(?:_([a-zA-Z]))?$/
  )
  if (match) {
    const fromGroundValue = parseFloat(match[1])
    const bottomLetter =
      match[2] && match[2].toUpperCase() !== 'N' ? match[2].toUpperCase() : ''
    const topLetter =
      match[3] && match[3].toUpperCase() !== 'N' ? match[3].toUpperCase() : ''
    if (!isNaN(fromGroundValue)) {
      const sectionBottomFromGround =
        props.legHeight +
        props.cabinetHeight / 2 +
        props.sectionY -
        props.panelHeight / 2
      const bottomSegment = fromGroundValue - sectionBottomFromGround
      const topSegment = props.panelHeight - bottomSegment
      resolvedSplitter = `${bottomLetter}${bottomSegment.toFixed(2)},${topLetter}${topSegment.toFixed(2)}`
    }
  }

  return (
    <Flex column gap={2}>
      <Typography variant="caption" color="textPrimary" sx={{ mb: -1 }}>
        Door Center Rail
      </Typography>
      <ToggleButtonGroup
        value={splitterMode}
        exclusive
        size="small"
        onChange={(_, val) => {
          if (!val) return
          if (val === 'fromGround') {
            const existing = props.normalMapSplitter || ''
            if (!existing.startsWith('g') && !existing.startsWith('G')) {
              const parts = existing.split(',')
              if (parts.length === 2) {
                const bottomStr = parts[0]
                const topStr = parts[1]
                const bottomMatch = bottomStr.match(/^([A-Za-z]?)([0-9.]+)$/)
                const topMatch = topStr.match(/^([A-Za-z]?)([0-9.]+)$/)

                let bottomLetter = 'N'
                let topLetter = 'N'
                let bottom = 0

                if (bottomMatch) {
                  bottomLetter = bottomMatch[1].toUpperCase() || 'N'
                  bottom = parseFloat(bottomMatch[2])
                }
                if (topMatch) {
                  topLetter = topMatch[1].toUpperCase() || 'N'
                }

                if (!isNaN(bottom)) {
                  const sectionBottomFromGround =
                    props.legHeight +
                    props.cabinetHeight / 2 +
                    props.sectionY -
                    props.panelHeight / 2
                  const newFromGround = parseFloat(
                    (sectionBottomFromGround + bottom).toFixed(2)
                  )
                  setFromGroundSplitterValue(newFromGround)
                  props.setSectionDoorSplitters(
                    `G${newFromGround.toFixed(2)}_${bottomLetter}_${topLetter}`
                  )
                }
              } else {
                props.setSectionDoorSplitters(
                  `G${fromGroundSplitterValue.toFixed(2)}_N_N`
                )
              }
            }
          } else if (val === 'manual') {
            const existing = props.normalMapSplitter || ''
            const match = existing.match(
              /^[gG]([0-9.]+)(?:_([a-zA-Z]))?(?:_([a-zA-Z]))?$/
            )
            if (match) {
              const fromGroundValue = parseFloat(match[1])
              const bottomLetter =
                match[2] && match[2].toUpperCase() !== 'N'
                  ? match[2].toUpperCase()
                  : ''
              const topLetter =
                match[3] && match[3].toUpperCase() !== 'N'
                  ? match[3].toUpperCase()
                  : ''
              if (!isNaN(fromGroundValue)) {
                const sectionBottomFromGround =
                  props.legHeight +
                  props.cabinetHeight / 2 +
                  props.sectionY -
                  props.panelHeight / 2
                const bottomSegment = fromGroundValue - sectionBottomFromGround
                const topSegment = props.panelHeight - bottomSegment
                props.setSectionDoorSplitters(
                  `${bottomLetter}${bottomSegment.toFixed(2)},${topLetter}${topSegment.toFixed(2)}`
                )
              } else {
                props.setSectionDoorSplitters('')
              }
            }
          }
          setSplitterMode(val)
        }}
        style={{ width: 180 }}
      >
        <ToggleButton
          value="manual"
          style={{ flex: 1, fontSize: 10, padding: '4px' }}
        >
          Manual
        </ToggleButton>
        <ToggleButton
          value="fromGround"
          style={{ flex: 1, fontSize: 10, padding: '4px' }}
        >
          From Ground
        </ToggleButton>
      </ToggleButtonGroup>

      {splitterMode === 'manual' ? (
        <TextField
          label={'Rails - ' + props.newDoorsDistY}
          variant="outlined"
          size="small"
          style={{ width: 180 }}
          onFocus={(e) => {
            handleFocus(e)
          }}
          onBlur={applyManualValue}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              applyManualValue()
            }
          }}
          value={localManualValue}
          onChange={(e) => {
            const acceptableSymbols = [
              '1',
              '2',
              '3',
              '4',
              '5',
              '6',
              '7',
              '8',
              '9',
              '0',
              '.',
              ',',
              'X',
              'x',
              'C',
              'c',
              'M',
              'm',
              'F',
              'f',
            ]
            if (
              e.target.value
                .split('')
                .every((s) => acceptableSymbols.includes(s))
            ) {
              setLocalManualValue(e.target.value.toUpperCase())
            }
          }}
        />
      ) : (
        <TextField
          label={`From Ground (${unit})`}
          variant="outlined"
          size="small"
          type="number"
          style={{ width: 180 }}
          onFocus={(e) => {
            handleFocus(e)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton
                  size="small"
                  title="Reset to default"
                  style={{
                    opacity: props.normalMapSplitter ? 1 : 0.3,
                    padding: 2,
                  }}
                  onClick={() => {
                    const defaultVal = 111.96
                    setFromGroundSplitterValue(defaultVal)
                    props.setSectionDoorSplitters(
                      `G${defaultVal.toFixed(2)}_N_N`
                    )
                  }}
                >
                  <SettingsBackupRestoreIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          value={parseFloat(
            (unit === 'in'
              ? cmToIn(fromGroundSplitterValue)
              : fromGroundSplitterValue
            ).toFixed(2)
          )}
          onChange={(e) => {
            const val = parseFloat(e.target.value)
            if (isNaN(val)) return
            const cmVal = unit === 'in' ? inToCm(val) : val
            setFromGroundSplitterValue(cmVal)

            const existingMatch = (props.normalMapSplitter || '').match(
              /^[gG][0-9.]+(?:_([a-zA-Z]))?(?:_([a-zA-Z]))?$/
            )
            const bottomLetter =
              existingMatch &&
              existingMatch[1] &&
              existingMatch[1].toUpperCase() !== 'N'
                ? existingMatch[1].toUpperCase()
                : 'N'
            const topLetter =
              existingMatch &&
              existingMatch[2] &&
              existingMatch[2].toUpperCase() !== 'N'
                ? existingMatch[2].toUpperCase()
                : 'N'

            props.setSectionDoorSplitters(
              `G${cmVal.toFixed(2)}_${bottomLetter}_${topLetter}`
            )
          }}
        />
      )}
      <TextField
        label={'Rails Overview'}
        variant="outlined"
        size="small"
        InputProps={{
          readOnly: true,
          style: { cursor: 'pointer' },
        }}
        style={{ width: 180 }}
        value={getNewDoorPiecesFromSplitters(
          resolvedSplitter,
          props.panelHeight,
          unit || 'cm'
        )
          .map((s) =>
            s.holeType === 'none'
              ? s.num.toFixed(2)
              : s.holeType[0].toUpperCase() + s.num.toFixed(2)
          )
          .join(',')}
        onClick={() => {
          props.setSectionDoorSplitters(
            getNewDoorPiecesFromSplitters(
              resolvedSplitter,
              props.panelHeight,
              'cm'
            )
              .map((s) =>
                s.holeType === 'none'
                  ? s.num
                  : s.holeType[0].toUpperCase() + s.num
              )
              .join(',')
          )
        }}
      />
    </Flex>
  )
}

interface IProps {
  isCorner: boolean
  isTwoDoorCorner: boolean
  cabinetDepth: number
  panelHeight: number
  left: number
  top: number
  shelfCount: number
  doorProps: DoorPropsType | undefined
  drawerProps: DrawerPropsType | undefined
  globalDoorType: DoorType
  globalHandlePosition: HandlePropsType['position']
  globalDoorVisualType: DoorVisualType
  panelVisualType: DoorVisualType
  drawerSlideType: DrawerSlideType

  setDividerMode(val: boolean): void
  removeTempShelves(): void
  addShelvesAsItem(count: number): void
  deleteShelves(): void
  unhideShelfItems(): void
  addTempDividers(): void
  removeTempDividers(): void
  setSectionDoor(type: DoorType): void
  setSectionDoorVisualType(type: DoorVisualType): void
  setSectionDrawer(): void
  deleteSectionDoor(): void
  deleteSectionDrawer(): void
  viewShelf(count: number): void
  viewDividers(count: number): void
  legHeight: number
  cabinetHeight: number
  sectionY: number
  handlePositionFromGroundDefault: number
  setSectionDoorHandlePosition(position: HandlePropsType['position']): void
  setSectionDoorHandlePositionX(positionX: HandlePropsType['positionX']): void
  setSectionDoorHandlePositionFromGroundValue(value: number | undefined): void
  setSectionDoorSplitters(value: string): void
  setSectionDrawerVisualType(type: DrawerVisualType): void
  setHandleOrientation(v: HandleOrientationType): void
  setDrawerHandleOrientation(v: HandleOrientationType): void
  setDrawerHandlePosition(position: HandlePropsType['position']): void
  setDrawerHandlePositionX(v: HandlePropsType['positionX']): void
}

const SectionActions: FC<IProps> = (props) => {
  const [shelfMode, setShelfMode] = useState(false)
  const [shelfCount, setShelfCount] = useState(
    props.shelfCount === 0 ? 1 : props.shelfCount
  )
  const [dividerMode, setDividerMode] = useState(false)
  const [dividerCount, setDividerCount] = useState(1)
  const [doorType, setDoorType] = useState<DoorType>(
    props.doorProps ? props.doorProps.type : props.globalDoorType
  )
  const [handlePos, setHandlePos] = useState<HandlePropsType['position']>(
    props.doorProps
      ? props.doorProps.handleProps.position
      : props.globalHandlePosition
  )
  const [handlePosX, setHandlePosX] = useState<HandlePropsType['positionX']>(
    props.doorProps ? props.doorProps.handleProps.positionX : 'left'
  )
  const [positionFromGroundValue, setPositionFromGroundValue] = useState<
    number | undefined
  >(props.doorProps?.handleProps.positionFromGroundValue)

  const [drawerHandlePos, setDrawerHandlePos] = useState<
    HandlePropsType['position']
  >(props.drawerProps ? props.drawerProps.handleProps.position : 'forDrawer')
  const [drawerHandlePosX, setDrawerHandlePosX] = useState<
    HandlePropsType['positionX']
  >(props.drawerProps ? props.drawerProps.handleProps.positionX : 'center')

  const [handleOrientation, setHandleOrientation] =
    useState<HandleOrientationType>(
      props.doorProps
        ? // @ts-ignore
          props.doorProps.handleProps.placement
        : 'vertical'
    )

  const [doorVisualType, setDoorVisualType] = useState<DoorVisualType>(
    props.doorProps?.visualType || props.panelVisualType
  )
  const [newDoorsDistY, setNewDoorsDistY] = useState('')

  const [isNewDoorVisualType, setIsNewDoorVisualType] = useState(
    doorVisualType.startsWith('#')
  )

  const [isShakerVisualType, setIsShakerVisualType] = useState(
    props.doorProps?.visualType.startsWith('shaker')
  )

  const [isB600R100, setIsB600R100] = useState(
    doorVisualType.includes('B600-R100')
  )

  const [isNewDrawerVisualType, setIsNewDrawerVisualType] = useState(
    props.drawerProps?.visualType?.startsWith('#')
  )

  const [isDrawerShakerVisualType, setIsDrawerShakerVisualType] = useState(
    props.drawerProps?.visualType?.startsWith('shaker')
  )

  const { enqueueSnackbar } = useSnackbar()
  const { unit } = useUnit()

  useEffect(() => {
    setIsNewDoorVisualType(doorVisualType.startsWith('#'))
    setIsShakerVisualType(props.doorProps?.visualType.startsWith('shaker'))

    if (doorVisualType.startsWith('#')) {
      const doorInnerType = doorVisualType.replaceAll('-', '').slice(1, 9)
      const distY =
        // @ts-ignore
        newDoorConfigs[doorInnerType].distXY * (unit === 'cm' ? 2.54 : 1)
      setNewDoorsDistY(distY.toFixed(2))
    }
  }, [doorVisualType, unit])

  useEffect(() => {
    setIsNewDrawerVisualType(props.drawerProps?.visualType?.startsWith('#'))
    setIsDrawerShakerVisualType(
      props.drawerProps?.visualType?.startsWith('shaker')
    )

    setDrawerHandlePosX(
      props.drawerProps ? props.drawerProps.handleProps.positionX : 'center'
    )

    setDrawerHandlePos(
      props.drawerProps ? props.drawerProps.handleProps.position : 'forDrawer'
    )
  }, [props.drawerProps])

  useEffect(() => {
    // debugger
    setShelfMode(false)
    setShelfCount(props.shelfCount === 0 ? 1 : props.shelfCount)
  }, [props.left, props.top, props.shelfCount])

  useEffect(() => {
    setDoorType(props.doorProps ? props.doorProps.type : props.globalDoorType)
  }, [props.doorProps, props.globalDoorType])

  useEffect(() => {
    setHandlePos(
      props.doorProps
        ? props.doorProps.handleProps.position
        : props.globalHandlePosition
    )
    setHandlePosX(
      props.doorProps ? props.doorProps.handleProps.positionX : 'left'
    )
    setHandleOrientation(
      props.doorProps ? props.doorProps.handleProps.orientation : 'vertical'
    )
    setPositionFromGroundValue(
      props.doorProps?.handleProps.positionFromGroundValue
    )
  }, [props.doorProps, props.globalHandlePosition])

  useEffect(() => {
    setDoorVisualType(props.doorProps?.visualType || props.panelVisualType)
  }, [props.doorProps, props.panelVisualType])

  useEffect(() => {
    setIsB600R100(doorVisualType.includes('B600-R100'))
  }, [doorVisualType])

  const turnOffShelvesMode = () => {
    props.removeTempShelves()
    props.unhideShelfItems()
    setShelfMode(false)
    setShelfCount(props.shelfCount)
  }

  const setHandlePosition = (value: HandlePositionTypes) => {
    props.setSectionDoorHandlePosition(value)
  }

  const configs = useContext(ConfigsContext)

  return (
    <>
      <Box
        style={{
          position: 'absolute',
          left: props.left + 35,
          top: props.top + 48 - 25, //48 is header height
          rowGap: 10,
          zIndex: 20,
        }}
        display="flex"
        flexDirection="column"
        // size='small'
        // color='secondary'
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (dividerMode) {
              setDividerMode(false)
            } else {
              setDividerMode(true)
              props.viewDividers(1)
            }

            if (shelfMode) turnOffShelvesMode()
          }}
        >
          Divider
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            if (!shelfMode === true) {
              setShelfMode(true)
              props.viewShelf(props.shelfCount === 0 ? 1 : props.shelfCount)
            } else {
              turnOffShelvesMode()
            }

            if (dividerMode) {
              setDividerMode(false)
              props.setDividerMode(false)
            }
          }}
        >
          Shelf
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            props.setSectionDoor(props.globalDoorType)
            props.setSectionDoorVisualType(props.panelVisualType)
          }}
        >
          Door
        </Button>
        {!props.isCorner && (
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (props.panelHeight < configs.drawerConfig.minimumHeight) {
                showMessage(
                  enqueueSnackbar,
                  "Drawer front panel height can't be smaller than " +
                    (unit === 'cm'
                      ? configs.drawerConfig.minimumHeight
                      : cmToIn(configs.drawerConfig.minimumHeight)) +
                    ' ' +
                    unit
                )
              } else {
                const minDepth =
                  minDepthOfCabByDrawerSlideType[props.drawerSlideType]

                if (props.cabinetDepth < minDepth) {
                  showMessage(
                    enqueueSnackbar,
                    `Drawer with ${props.drawerSlideType} slide can't be added if cabinet depth smaller than ` +
                      (unit === 'cm' ? minDepth : cmToIn(minDepth)) +
                      ' ' +
                      unit
                  )
                } else {
                  props.setSectionDrawer()
                  let visualType = props.panelVisualType as any
                  if (visualType === 'vGroove') {
                    visualType = 'standard'
                  }
                  props.setSectionDrawerVisualType(visualType)
                }
              }
            }}
          >
            Drawer
          </Button>
        )}
      </Box>

      <Slide direction="up" in={!!props.drawerProps}>
        <Paper
          style={{
            position: 'absolute',
            padding: 8,
            right: 488,
            bottom: 64, //48 is header height
            display: 'flex',
            zIndex: 20,
            // alignItems: 'center',
          }}
          sx={{
            flexDirection: 'column',
            ml: 1,
          }}
        >
          <Flex centerY>
            <Typography style={{ fontSize: 18, flexGrow: 1 }}>
              Drawer Options
            </Typography>
            <Fab
              style={{
                maxWidth: 25,
                maxHeight: 25,
                minWidth: 25,
                minHeight: 25,
              }}
              color="secondary"
              onClick={() => {
                props.deleteSectionDrawer()
              }}
            >
              <DeleteIcon />
            </Fab>
          </Flex>
          <Divider
            sx={{
              margin: '8px -8px',
            }}
          />
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Qty of Pulls
          </Typography>
          <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
            <Tooltip title="One Pull">
              <span>
                <TypeButton
                  selectedType={
                    props.drawerProps?.handleProps.orientation || 'vertical'
                  }
                  type="vertical"
                  setType={props.setDrawerHandleOrientation}
                >
                  <DrawerSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Two Pulls">
              <span>
                <TypeButton
                  selectedType={
                    props.drawerProps?.handleProps.orientation || 'vertical'
                  }
                  type="double_horizontal"
                  setType={props.setDrawerHandleOrientation}
                >
                  <DrawerDoubleSVG />
                </TypeButton>
              </span>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Pull Location
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={0.5}
          >
            <Tooltip title="Top">
              <span>
                <TypeButton
                  selectedType={
                    typeof drawerHandlePos === 'string'
                      ? drawerHandlePos
                      : 'none'
                  }
                  type="forDrawer"
                  setType={(v) => props.setDrawerHandlePosition('forDrawer')}
                >
                  <DrawerSVG />
                </TypeButton>
              </span>
            </Tooltip>

            <Tooltip title="Center">
              <span>
                <TypeButton
                  selectedType={
                    typeof drawerHandlePos === 'string'
                      ? drawerHandlePos
                      : 'none'
                  }
                  type="forDrawerCenter"
                  setType={(v) =>
                    props.setDrawerHandlePosition('forDrawerCenter')
                  }
                >
                  <DrawerCenterSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Pull position from drawer bottom">
              <TextField
                label={unit}
                variant="outlined"
                size="small"
                type="number"
                style={{
                  width: 80,
                }}
                onFocus={(e) => {
                  handleFocus(e)
                }}
                value={
                  typeof drawerHandlePos === 'number'
                    ? unit === 'cm'
                      ? drawerHandlePos
                      : cmToIn(drawerHandlePos)
                    : -1
                }
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value)
                  props.setDrawerHandlePosition(
                    unit === 'cm' ? newValue : inToCm(newValue)
                  )
                }}
              />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Pull Horizontal Location
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={0.5}
          >
            <Tooltip title="Left">
              <span>
                <TypeButton
                  selectedType={
                    typeof drawerHandlePosX === 'string'
                      ? drawerHandlePosX
                      : 'none'
                  }
                  type="left"
                  setType={(v: HandlePositionXTypes) =>
                    props.setDrawerHandlePositionX(v)
                  }
                >
                  <DrawerLeftSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Center">
              <span>
                <TypeButton
                  selectedType={
                    typeof drawerHandlePosX === 'string'
                      ? drawerHandlePosX
                      : 'none'
                  }
                  type="center"
                  setType={(v: HandlePositionXTypes) =>
                    props.setDrawerHandlePositionX(v)
                  }
                >
                  <DrawerSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Right">
              <span>
                <TypeButton
                  selectedType={
                    typeof drawerHandlePosX === 'string'
                      ? drawerHandlePosX
                      : 'none'
                  }
                  type="right"
                  setType={(v: HandlePositionXTypes) =>
                    props.setDrawerHandlePositionX(v)
                  }
                >
                  <DrawerRightSVG />
                </TypeButton>
              </span>
            </Tooltip>

            <Tooltip title="Position from left of the drawer">
              <TextField
                label={unit}
                variant="outlined"
                size="small"
                type="number"
                style={{
                  width: 80,
                }}
                onFocus={(e) => {
                  handleFocus(e)
                }}
                value={
                  typeof drawerHandlePosX === 'number'
                    ? unit === 'cm'
                      ? drawerHandlePosX
                      : cmToIn(drawerHandlePosX)
                    : -1
                }
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value)
                  props.setDrawerHandlePositionX(
                    unit === 'cm' ? newValue : inToCm(newValue)
                  )
                }}
              />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Drawer Front Style
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ marginTop: 4, columnGap: 5 }}
          >
            <Tooltip title="Workspace">
              <span>
                <TypeButton
                  selectedType={props.drawerProps?.visualType || 'standard'}
                  type="standard"
                  setType={props.setSectionDrawerVisualType}
                >
                  <DrawerSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="5 pc Melamine">
              <span>
                <TypeButton
                  selectedType={props.drawerProps?.visualType || 'standard'}
                  type="fivePieces"
                  setType={props.setSectionDrawerVisualType}
                >
                  <DrawerSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Powder Coated">
              <span>
                <TypeButton
                  selectedType={props.drawerProps?.visualType || 'standard'}
                  type="#"
                  setType={() => {
                    props.setSectionDrawerVisualType(newDoorVisualTypeValues[0])
                  }}
                >
                  <DrawerNewSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Flat Panel">
              <span>
                <TypeButton
                  selectedType={props.drawerProps?.visualType || 'standard'}
                  type="shaker"
                  setType={() => props.setSectionDrawerVisualType('shaker_h')}
                >
                  <DrawerSVG />
                </TypeButton>
              </span>
            </Tooltip>
          </Box>
          {isNewDrawerVisualType && (
            <Flex sx={{ mt: 2 }} column gap={1}>
              <FormControl size="small" sx={{ width: 180 }}>
                <InputLabel>New Drawer Visual Type</InputLabel>
                <Select
                  defaultValue={newDoorVisualTypeValues[0]}
                  value={
                    props.drawerProps?.visualType || newDoorVisualTypeValues[0]
                  }
                  label={'New Drawer Visual Type'}
                  onChange={(e) => {
                    props.setSectionDrawerVisualType(
                      e.target.value as DrawerVisualType
                    )
                  }}
                >
                  {newDoorVisualTypeValues.map((v) => (
                    <MenuItem value={v} key={'dv' + v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Flex>
          )}
          {isDrawerShakerVisualType && (
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={
                  props.drawerProps?.visualType
                    ? props.drawerProps?.visualType[
                        props.drawerProps?.visualType.length - 1
                      ]
                    : ''
                }
                onChange={(e) => {
                  props.setSectionDrawerVisualType(
                    ('shaker_' + e.target.value) as DrawerVisualType
                  )
                }}
              >
                <FormControlLabel
                  value="h"
                  control={<Radio color="primary" />}
                  label="Horizontal"
                />
                <FormControlLabel
                  value="v"
                  control={<Radio color="primary" />}
                  label="Vertical"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Paper>
      </Slide>

      {/* <Slide direction="left" in={!!props.drawerProps}>
        <Box
          style={{
            position: 'absolute',
            height: 36,
            left: props.left + 35 + 100,
            top: props.top + 48 - 25 + 36 * 3 + 10 * 3, //48 is header height
            display: 'flex',
            zIndex: 20,
            // alignItems: 'center',
          }}
          sx={{
            flexDirection: 'column',
            ml: 1,
          }}
        >
          <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
            <TypeButton
              selectedType={
                props.drawerProps?.handleProps.orientation || 'vertical'
              }
              type="vertical"
              setType={props.setDrawerHandleOrientation}
            >
              <DrawerSVG />
            </TypeButton>
            <TypeButton
              selectedType={
                props.drawerProps?.handleProps.orientation || 'vertical'
              }
              type="double_horizontal"
              setType={props.setDrawerHandleOrientation}
            >
              <DrawerDoubleSVG />
            </TypeButton>
            <Fab
              style={{
                maxWidth: 25,
                maxHeight: 25,
                minWidth: 25,
                minHeight: 25,
              }}
              color="secondary"
              onClick={() => {
                props.deleteSectionDrawer()
              }}
            >
              <DeleteIcon />
            </Fab>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            <TypeButton
              selectedType={
                typeof drawerHandlePos === 'string' ? drawerHandlePos : 'none'
              }
              type="forDrawer"
              setType={(v) => props.setDrawerHandlePosition('forDrawer')}
            >
              <DrawerSVG />
            </TypeButton>

            <TypeButton
              selectedType={
                typeof drawerHandlePos === 'string' ? drawerHandlePos : 'none'
              }
              type="forDrawerCenter"
              setType={(v) => props.setDrawerHandlePosition('forDrawerCenter')}
            >
              <DrawerCenterSVG />
            </TypeButton>
            <TextField
              label={unit}
              variant="outlined"
              size="small"
              type="number"
              style={{
                width: 80,
              }}
              onFocus={(e) => {
                handleFocus(e)
              }}
              value={
                typeof drawerHandlePos === 'number'
                  ? unit === 'cm'
                    ? drawerHandlePos
                    : cmToIn(drawerHandlePos)
                  : -1
              }
              onChange={(e) => {
                const newValue = parseFloat(e.target.value)
                props.setDrawerHandlePosition(
                  unit === 'cm' ? newValue : inToCm(newValue)
                )
              }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            <TypeButton
              selectedType={
                typeof drawerHandlePosX === 'string' ? drawerHandlePosX : 'none'
              }
              type="left"
              setType={(v: HandlePositionXTypes) =>
                props.setDrawerHandlePositionX(v)
              }
            >
              <DrawerLeftSVG />
            </TypeButton>
            <TypeButton
              selectedType={
                typeof drawerHandlePosX === 'string' ? drawerHandlePosX : 'none'
              }
              type="center"
              setType={(v: HandlePositionXTypes) =>
                props.setDrawerHandlePositionX(v)
              }
            >
              <DrawerSVG />
            </TypeButton>
            <TypeButton
              selectedType={
                typeof drawerHandlePosX === 'string' ? drawerHandlePosX : 'none'
              }
              type="right"
              setType={(v: HandlePositionXTypes) =>
                props.setDrawerHandlePositionX(v)
              }
            >
              <DrawerRightSVG />
            </TypeButton>

            <TextField
              label={unit}
              variant="outlined"
              size="small"
              type="number"
              style={{
                width: 80,
              }}
              onFocus={(e) => {
                handleFocus(e)
              }}
              value={
                typeof drawerHandlePosX === 'number'
                  ? unit === 'cm'
                    ? drawerHandlePosX
                    : cmToIn(drawerHandlePosX)
                  : -1
              }
              onChange={(e) => {
                const newValue = parseFloat(e.target.value)
                props.setDrawerHandlePositionX(
                  unit === 'cm' ? newValue : inToCm(newValue)
                )
              }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            style={{ marginTop: 8, columnGap: 5 }}
          >
            <TypeButton
              selectedType={props.drawerProps?.visualType || 'standard'}
              type="standard"
              setType={props.setSectionDrawerVisualType}
            >
              <DrawerSVG />
            </TypeButton>
            <TypeButton
              selectedType={props.drawerProps?.visualType || 'standard'}
              type="fivePieces"
              setType={props.setSectionDrawerVisualType}
            >
              <DrawerSVG />
            </TypeButton>
            <TypeButton
              selectedType={props.drawerProps?.visualType || 'standard'}
              type="#"
              setType={() => {
                props.setSectionDrawerVisualType(newDoorVisualTypeValues[0])
              }}
            >
              <DrawerNewSVG />
            </TypeButton>
            <TypeButton
              selectedType={props.drawerProps?.visualType || 'standard'}
              type="shaker"
              setType={() => props.setSectionDrawerVisualType('shaker_h')}
            >
              <DrawerSVG />
            </TypeButton>
          </Box>
          {isNewDrawerVisualType && (
            <Flex sx={{ mt: 2 }} column gap={1}>
              <FormControl size="small" sx={{ width: 180 }}>
                <InputLabel>New Drawer Visual Type</InputLabel>
                <Select
                  defaultValue={newDoorVisualTypeValues[0]}
                  value={
                    props.drawerProps?.visualType || newDoorVisualTypeValues[0]
                  }
                  label={'New Drawer Visual Type'}
                  onChange={(e) => {
                    props.setSectionDrawerVisualType(
                      e.target.value as DrawerVisualType
                    )
                  }}
                >
                  {newDoorVisualTypeValues.map((v) => (
                    <MenuItem value={v} key={'dv' + v}>
                      {v}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Flex>
          )}
          {isDrawerShakerVisualType && (
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={
                  props.drawerProps?.visualType
                    ? props.drawerProps?.visualType[
                        props.drawerProps?.visualType.length - 1
                      ]
                    : ''
                }
                onChange={(e) => {
                  props.setSectionDrawerVisualType(
                    ('shaker_' + e.target.value) as DrawerVisualType
                  )
                }}
              >
                <FormControlLabel
                  value="h"
                  control={<Radio color="primary" />}
                  label="Horizontal"
                />
                <FormControlLabel
                  value="v"
                  control={<Radio color="primary" />}
                  label="Vertical"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Box>
      </Slide> */}

      <Slide direction="up" in={!!props.doorProps}>
        <Paper
          style={{
            position: 'absolute',
            padding: 8,
            right: 488,
            bottom: 64, //48 is header height
            display: 'flex',
            zIndex: 20,
            // alignItems: 'center',
          }}
          sx={{
            flexDirection: 'column',
            ml: 1,
          }}
        >
          <Flex centerY>
            <Typography style={{ fontSize: 18, flexGrow: 1 }}>
              Door Options
            </Typography>
            <Fab
              style={{
                maxWidth: 25,
                maxHeight: 25,
                minWidth: 25,
                minHeight: 25,
              }}
              color="secondary"
              onClick={() => {
                props.deleteSectionDoor()
              }}
            >
              <DeleteIcon />
            </Fab>
          </Flex>
          <Divider
            sx={{
              margin: '8px -8px',
            }}
          />
          <Typography variant="caption" color="textPrimary">
            Door Type
          </Typography>
          <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
            <Tooltip title="Right Door">
              <span>
                <TypeButton
                  selectedType={doorType}
                  type="right"
                  setType={props.setSectionDoor}
                >
                  <LeftDoorSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Left Door">
              <span>
                <TypeButton
                  selectedType={doorType}
                  type="left"
                  setType={props.setSectionDoor}
                >
                  <RightDoorSVG />
                </TypeButton>
              </span>
            </Tooltip>
            {!props.isCorner && (
              <Tooltip title="Pair of Doors">
                <span>
                  <TypeButton
                    selectedType={doorType}
                    type="double"
                    setType={props.setSectionDoor}
                  >
                    <DoubleDoorSVG />
                  </TypeButton>
                </span>
              </Tooltip>
            )}
            {!props.isTwoDoorCorner && (
              <Tooltip title="Tip Up Door">
                <span>
                  <TypeButton
                    selectedType={doorType}
                    type="liftUp"
                    setType={props.setSectionDoor}
                  >
                    <LiftUpDoorSVG />
                  </TypeButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Pull Location
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={0.5}
          >
            <Tooltip title="Bottom">
              <span>
                <TypeButton
                  selectedType={
                    typeof handlePos === 'string' ? handlePos : 'none'
                  }
                  type="bottom"
                  setType={setHandlePosition}
                >
                  <BottomHandleSVG />
                </TypeButton>
              </span>
            </Tooltip>
            {doorType !== 'liftUp' && (
              <>
                <Tooltip title="Middle">
                  <span>
                    <TypeButton
                      selectedType={
                        typeof handlePos === 'string' ? handlePos : 'none'
                      }
                      type="center"
                      setType={setHandlePosition}
                    >
                      <DoubleDoorSVG />
                    </TypeButton>
                  </span>
                </Tooltip>
                <Tooltip title="Top">
                  <span>
                    <TypeButton
                      selectedType={
                        typeof handlePos === 'string' ? handlePos : 'none'
                      }
                      type="top"
                      setType={setHandlePosition}
                    >
                      <TopHandleSVG />
                    </TypeButton>
                  </span>
                </Tooltip>
              </>
            )}

            <Tooltip title="Pull position from door bottom">
              <TextField
                label={unit}
                variant="outlined"
                size="small"
                type="number"
                style={{
                  width: 80,
                }}
                onFocus={(e) => {
                  handleFocus(e)
                }}
                value={
                  typeof handlePos === 'number'
                    ? unit === 'cm'
                      ? handlePos
                      : cmToIn(handlePos)
                    : -1
                }
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value)
                  props.setSectionDoorHandlePosition(
                    unit === 'cm' ? newValue : inToCm(newValue)
                  )
                }}
              />
            </Tooltip>

            <Tooltip title="Adjustable or specific dimension off the floor">
              <span>
                <TypeButton
                  selectedType={
                    typeof handlePos === 'string' ? handlePos : 'none'
                  }
                  type="fromGround"
                  setType={setHandlePosition}
                >
                  <FromGroundSVG />
                </TypeButton>
              </span>
            </Tooltip>
          </Box>
          {(handlePos === 'top' || handlePos === 'bottom') && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5, lineHeight: 1.2, display: 'block' }}
            >
              Top and bottom is 3" from the edge of door to the first hole.
            </Typography>
          )}
          {typeof handlePos === 'number' && (
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ mt: 0.5, lineHeight: 1.2, display: 'block' }}
            >
              Number option is from bottom of door to the first hole.
            </Typography>
          )}
          {handlePos === 'fromGround' && (
            <Box mt={1}>
              <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
                <TextField
                  label={`From Ground (${unit})`}
                  variant="outlined"
                  size="small"
                  type="number"
                  style={{ width: 175 }}
                  onFocus={(e) => {
                    handleFocus(e)
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <IconButton
                          size="small"
                          title="Reset to default"
                          style={{
                            opacity:
                              positionFromGroundValue !== undefined ? 1 : 0.3,
                            padding: 2,
                          }}
                          onClick={() => {
                            setPositionFromGroundValue(undefined)
                            props.setSectionDoorHandlePositionFromGroundValue(
                              undefined
                            )
                          }}
                        >
                          <SettingsBackupRestoreIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  value={
                    positionFromGroundValue !== undefined
                      ? unit === 'cm'
                        ? positionFromGroundValue
                        : cmToIn(positionFromGroundValue)
                      : unit === 'cm'
                        ? props.handlePositionFromGroundDefault
                        : cmToIn(props.handlePositionFromGroundDefault)
                  }
                  onChange={(e) => {
                    const newValue = parseFloat(e.target.value)
                    const cmValue = unit === 'cm' ? newValue : inToCm(newValue)
                    setPositionFromGroundValue(cmValue)
                    props.setSectionDoorHandlePositionFromGroundValue(cmValue)
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ display: 'block', mt: 0.5, lineHeight: 1.2 }}
              >
                Floor to pull (111.96) is to the center of the pull
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Pull Horizontal Location
          </Typography>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            {!(props.isTwoDoorCorner && doorType === 'left') && (
              <Tooltip title="Left">
                <span>
                  <TypeButton
                    selectedType={
                      typeof handlePosX === 'string' ? handlePosX : 'none'
                    }
                    type="left"
                    setType={(v: HandlePositionXTypes) =>
                      props.setSectionDoorHandlePositionX(v)
                    }
                  >
                    <LeftDoorSVG />
                  </TypeButton>
                </span>
              </Tooltip>
            )}
            {!props.isTwoDoorCorner && (
              <Tooltip title="Center">
                <span>
                  <TypeButton
                    selectedType={
                      typeof handlePosX === 'string' ? handlePosX : 'none'
                    }
                    type="center"
                    setType={(v: HandlePositionXTypes) =>
                      props.setSectionDoorHandlePositionX(v)
                    }
                  >
                    <CenterHandleSVG />
                  </TypeButton>
                </span>
              </Tooltip>
            )}
            {!(props.isTwoDoorCorner && doorType === 'right') && (
              <Tooltip title="Right">
                <span>
                  <TypeButton
                    selectedType={
                      typeof handlePosX === 'string' ? handlePosX : 'none'
                    }
                    type="right"
                    setType={(v: HandlePositionXTypes) =>
                      props.setSectionDoorHandlePositionX(v)
                    }
                  >
                    <RightDoorSVG />
                  </TypeButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title="Position from left of the door">
              <TextField
                label={unit}
                variant="outlined"
                size="small"
                type="number"
                style={{
                  width: 80,
                }}
                onFocus={(e) => {
                  handleFocus(e)
                }}
                value={
                  typeof handlePosX === 'number'
                    ? unit === 'cm'
                      ? handlePosX
                      : cmToIn(handlePosX)
                    : -1
                }
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value)
                  props.setSectionDoorHandlePositionX(
                    unit === 'cm' ? newValue : inToCm(newValue)
                  )
                }}
              />
            </Tooltip>
          </Box>
          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Pull Orientation
          </Typography>
          {doorType === 'liftUp' ? (
            <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
              <TypeButton
                selectedType={handleOrientation}
                type="vertical"
                setType={props.setHandleOrientation}
              >
                <DrawerSVG />
              </TypeButton>
              <TypeButton
                selectedType={handleOrientation}
                type="double_horizontal"
                setType={props.setHandleOrientation}
              >
                <DrawerDoubleSVG />
              </TypeButton>
            </Box>
          ) : (
            <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
              <TypeButton
                selectedType={handleOrientation}
                type="vertical"
                setType={props.setHandleOrientation}
              >
                <CenterHandleSVG />
              </TypeButton>
              <TypeButton
                selectedType={handleOrientation}
                type="horizontal"
                setType={props.setHandleOrientation}
              >
                <HorizontalHandleSVG />
              </TypeButton>
            </Box>
          )}

          <Typography variant="caption" color="textPrimary" sx={{ mt: 1 }}>
            Change Door Style
          </Typography>
          <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
            <Tooltip title="V-Groove Door">
              <span>
                <TypeButton
                  selectedType={doorVisualType}
                  type="vGroove"
                  setType={props.setSectionDoorVisualType}
                >
                  <VGrooveDoorSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="5 piece Melamine Door">
              <span>
                <TypeButton
                  selectedType={doorVisualType}
                  type="fivePieces"
                  setType={props.setSectionDoorVisualType}
                >
                  <FivePiecesDoorSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Powder Coated Doors">
              <span>
                <TypeButton
                  selectedType={doorVisualType}
                  type="#"
                  setType={() => {
                    props.setSectionDoorVisualType(newDoorVisualTypeValues[0])
                  }}
                >
                  <NewDoorSVG />
                </TypeButton>
              </span>
            </Tooltip>
            <Tooltip title="Flat Panel">
              <span>
                <TypeButton
                  selectedType={doorVisualType}
                  type="shaker"
                  setType={() => props.setSectionDoorVisualType('shaker_h')}
                >
                  <ShakerDoorSVG />
                </TypeButton>
              </span>
            </Tooltip>
          </Box>
          {(isNewDoorVisualType || doorVisualType === 'fivePieces') && (
            <Flex sx={{ mt: 2 }} column gap={1}>
              {isNewDoorVisualType ? (
                <FormControl size="small" sx={{ width: 180 }}>
                  <InputLabel>New Door Visual Type</InputLabel>
                  <Select
                    defaultValue={newDoorVisualTypeValues[0]}
                    value={doorVisualType}
                    label={'New Door Visual Type'}
                    onChange={(e) => {
                      props.setSectionDoorVisualType(
                        e.target.value as DoorVisualType
                      )
                    }}
                  >
                    {newDoorVisualTypeValues.map((v) => (
                      <MenuItem value={v} key={'dv' + v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <></>
              )}
              <SplitterInput
                sectionNumber={props.doorProps?.sectionNumber}
                normalMapSplitter={props.doorProps?.normalMapSplitter}
                panelHeight={props.panelHeight}
                legHeight={props.legHeight}
                cabinetHeight={props.cabinetHeight}
                sectionY={props.sectionY}
                newDoorsDistY={newDoorsDistY}
                setSectionDoorSplitters={props.setSectionDoorSplitters}
              />
            </Flex>
          )}

          {isShakerVisualType && (
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={doorVisualType[doorVisualType.length - 1]}
                onChange={(e) => {
                  props.setSectionDoorVisualType(
                    ('shaker_' + e.target.value) as DoorVisualType
                  )
                }}
              >
                <FormControlLabel
                  value="h"
                  control={<Radio color="primary" />}
                  label="Horizontal"
                />
                <FormControlLabel
                  value="v"
                  control={<Radio color="primary" />}
                  label="Vertical"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Paper>
      </Slide>

      {/* <Slide direction="left" in={!!props.doorProps}>
        <Paper
          style={{
            position: 'absolute',
            left: props.left + 35 + 100,
            top: props.top, //+ 48 - 25 + 36 * 2 + 10 * 2, //48 is header height
            zIndex: 20,
            padding: 8,
          }}
        >
          <Box display="flex" alignItems="center" style={{ columnGap: 5 }}>
            <TypeButton
              selectedType={doorType}
              type="right"
              setType={props.setSectionDoor}
            >
              <LeftDoorSVG />
            </TypeButton>
            <TypeButton
              selectedType={doorType}
              type="left"
              setType={props.setSectionDoor}
            >
              <RightDoorSVG />
            </TypeButton>
            {!props.isCorner && (
              <TypeButton
                selectedType={doorType}
                type="double"
                setType={props.setSectionDoor}
              >
                <DoubleDoorSVG />
              </TypeButton>
            )}
            <TypeButton
              selectedType={doorType}
              type="liftUp"
              setType={props.setSectionDoor}
            >
              <LiftUpDoorSVG />
            </TypeButton>

            <Fab
              style={{
                maxWidth: 25,
                maxHeight: 25,
                minWidth: 25,
                minHeight: 25,
              }}
              color="secondary"
              onClick={() => {
                props.deleteSectionDoor()
              }}
            >
              <DeleteIcon />
            </Fab>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            <TypeButton
              selectedType={typeof handlePos === 'string' ? handlePos : 'none'}
              type="bottom"
              setType={setHandlePosition}
            >
              <BottomHandleSVG />
            </TypeButton>
            <TypeButton
              selectedType={typeof handlePos === 'string' ? handlePos : 'none'}
              type="center"
              setType={setHandlePosition}
            >
              <DoubleDoorSVG />
            </TypeButton>
            <TypeButton
              selectedType={typeof handlePos === 'string' ? handlePos : 'none'}
              type="top"
              setType={setHandlePosition}
            >
              <TopHandleSVG />
            </TypeButton>

            <TextField
              label={unit}
              variant="outlined"
              size="small"
              type="number"
              style={{
                width: 80,
              }}
              onFocus={(e) => {
                handleFocus(e)
              }}
              value={
                typeof handlePos === 'number'
                  ? unit === 'cm'
                    ? handlePos
                    : cmToIn(handlePos)
                  : -1
              }
              onChange={(e) => {
                const newValue = parseFloat(e.target.value)
                props.setSectionDoorHandlePosition(
                  unit === 'cm' ? newValue : inToCm(newValue)
                )
              }}
            />

            <TypeButton
              selectedType={typeof handlePos === 'string' ? handlePos : 'none'}
              type="fromGround"
              setType={setHandlePosition}
            >
              <FromGroundSVG />
            </TypeButton>
          </Box>
          {handlePos === 'fromGround' && (
            <Box display="flex" alignItems="center" style={{ columnGap: 5 }} mt={1}>
              <TextField
                label={`From Ground (${unit})`}
                variant="outlined"
                size="small"
                type="number"
                style={{ width: 150 }}
                onFocus={(e) => {
                  handleFocus(e)
                }}
                value={
                  positionFromGroundValue !== undefined
                    ? unit === 'cm'
                      ? positionFromGroundValue
                      : cmToIn(positionFromGroundValue)
                    : unit === 'cm'
                      ? props.handlePositionFromGroundDefault
                      : cmToIn(props.handlePositionFromGroundDefault)
                }
                onChange={(e) => {
                  const newValue = parseFloat(e.target.value)
                  const cmValue = unit === 'cm' ? newValue : inToCm(newValue)
                  setPositionFromGroundValue(cmValue)
                  props.setSectionDoorHandlePositionFromGroundValue(cmValue)
                }}
              />
            </Box>
          )}
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            <TypeButton
              selectedType={
                typeof handlePosX === 'string' ? handlePosX : 'none'
              }
              type="left"
              setType={(v: HandlePositionXTypes) =>
                props.setSectionDoorHandlePositionX(v)
              }
            >
              <LeftDoorSVG />
            </TypeButton>
            <TypeButton
              selectedType={
                typeof handlePosX === 'string' ? handlePosX : 'none'
              }
              type="center"
              setType={(v: HandlePositionXTypes) =>
                props.setSectionDoorHandlePositionX(v)
              }
            >
              <CenterHandleSVG />
            </TypeButton>
            <TypeButton
              selectedType={
                typeof handlePosX === 'string' ? handlePosX : 'none'
              }
              type="right"
              setType={(v: HandlePositionXTypes) =>
                props.setSectionDoorHandlePositionX(v)
              }
            >
              <RightDoorSVG />
            </TypeButton>

            <TextField
              label={unit}
              variant="outlined"
              size="small"
              type="number"
              style={{
                width: 80,
              }}
              onFocus={(e) => {
                handleFocus(e)
              }}
              value={
                typeof handlePosX === 'number'
                  ? unit === 'cm'
                    ? handlePosX
                    : cmToIn(handlePosX)
                  : -1
              }
              onChange={(e) => {
                const newValue = parseFloat(e.target.value)
                props.setSectionDoorHandlePositionX(
                  unit === 'cm' ? newValue : inToCm(newValue)
                )
              }}
            />
          </Box>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            <TypeButton
              selectedType={handleOrientation}
              type="vertical"
              setType={props.setHandleOrientation}
            >
              <CenterHandleSVG />
            </TypeButton>
            <TypeButton
              selectedType={handleOrientation}
              type="horizontal"
              setType={props.setHandleOrientation}
            >
              <HorizontalHandleSVG />
            </TypeButton>
          </Box>
          <Box
            display="flex"
            alignItems="center"
            style={{ columnGap: 5 }}
            mt={1}
          >
            <TypeButton
              selectedType={doorVisualType}
              type="vGroove"
              setType={props.setSectionDoorVisualType}
            >
              <VGrooveDoorSVG />
            </TypeButton>
            <TypeButton
              selectedType={doorVisualType}
              type="fivePieces"
              setType={props.setSectionDoorVisualType}
            >
              <FivePiecesDoorSVG />
            </TypeButton>
            <TypeButton
              selectedType={doorVisualType}
              type="#"
              setType={() => {
                props.setSectionDoorVisualType(newDoorVisualTypeValues[0])
              }}
            >
              <NewDoorSVG />
            </TypeButton>
            <TypeButton
              selectedType={doorVisualType}
              type="shaker"
              setType={() => props.setSectionDoorVisualType('shaker_h')}
            >
              <ShakerDoorSVG />
            </TypeButton>
          </Box>
          {(isNewDoorVisualType || doorVisualType === 'fivePieces') && (
            <Flex sx={{ mt: 2 }} column gap={1}>
              {isNewDoorVisualType ? (
                <FormControl size="small" sx={{ width: 180 }}>
                  <InputLabel>New Door Visual Type</InputLabel>
                  <Select
                    defaultValue={newDoorVisualTypeValues[0]}
                    value={doorVisualType}
                    label={'New Door Visual Type'}
                    onChange={(e) => {
                      props.setSectionDoorVisualType(
                        e.target.value as DoorVisualType
                      )
                    }}
                  >
                    {newDoorVisualTypeValues.map((v) => (
                      <MenuItem value={v} key={'dv' + v}>
                        {v}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : (
                <></>
              )}
              <TextField
                label={'Splitters - ' + newDoorsDistY}
                variant="outlined"
                size="small"
                style={{
                  width: 180,
                }}
                onFocus={(e) => {
                  handleFocus(e)
                }}
                value={(() => {
                  let value = props.doorProps?.normalMapSplitter || ''
                  // console.log('-------', value)

                  if (unit === 'in') {
                    const numbers = value.split(',')
                    const numbersInInch = []

                    for (const num of numbers) {
                      const n = parseFloat(num)

                      if (isNaN(n)) {
                        const letter = num[0]
                        const _num = num.slice(1)
                        const n = parseFloat(_num)

                        if (isNaN(n)) {
                          numbersInInch.push(num)
                        } else {
                          numbersInInch.push(letter + cmToIn(n))
                        }
                      } else {
                        numbersInInch.push(cmToIn(n))
                      }
                    }

                    value = numbersInInch.join(',')
                  }
                  return value
                })()}
                onChange={(e) => {
                  const acceptableSymbols = [
                    '1',
                    '2',
                    '3',
                    '4',
                    '5',
                    '6',
                    '7',
                    '8',
                    '9',
                    '0',
                    '.',
                    ',',
                    'X',
                    'x',
                    'C',
                    'c',
                    'M',
                    'm',
                    'F',
                    'f',
                  ]

                  if (
                    e.target.value
                      .split('')
                      .every((s) => acceptableSymbols.includes(s))
                  ) {
                    let value = e.target.value.toUpperCase()

                    if (unit === 'in') {
                      const numbers = value.split(',')
                      const numbersInInch = []

                      for (const num of numbers) {
                        const n = parseFloat(num)

                        if (isNaN(n)) {
                          const letter = num[0]
                          const _num = num.slice(1)
                          const n = parseFloat(_num)

                          if (isNaN(n)) {
                            numbersInInch.push(num)
                          } else {
                            numbersInInch.push(letter + inToCm(n))
                          }
                        } else {
                          numbersInInch.push(inToCm(n))
                        }
                      }

                      value = numbersInInch.join(',')
                    }

                    props.setSectionDoorSplitters(value)
                  }
                }}
              />
              <TextField
                label={'Splitters Overview'}
                variant="outlined"
                size="small"
                style={{
                  width: 180,
                }}
                value={getNewDoorPiecesFromSplitters(
                  props.doorProps?.normalMapSplitter || '',
                  props.panelHeight,
                  unit || 'cm'
                )
                  .map((s) =>
                    s.holeType === 'none'
                      ? s.num
                      : s.holeType[0].toUpperCase() + s.num
                  )
                  .join(',')}
                onClick={() => {
                  props.setSectionDoorSplitters(
                    getNewDoorPiecesFromSplitters(
                      props.doorProps?.normalMapSplitter || '',
                      props.panelHeight,
                      'cm'
                    )
                      .map((s) =>
                        s.holeType === 'none'
                          ? s.num
                          : s.holeType[0].toUpperCase() + s.num
                      )
                      .join(',')
                  )
                }}
              />
            </Flex>
          )}

          {isShakerVisualType && (
            <FormControl component="fieldset">
              <RadioGroup
                row
                value={doorVisualType[doorVisualType.length - 1]}
                onChange={(e) => {
                  props.setSectionDoorVisualType(
                    ('shaker_' + e.target.value) as DoorVisualType
                  )
                }}
              >
                <FormControlLabel
                  value="h"
                  control={<Radio color="primary" />}
                  label="Horizontal"
                />
                <FormControlLabel
                  value="v"
                  control={<Radio color="primary" />}
                  label="Vertical"
                />
              </RadioGroup>
            </FormControl>
          )}
        </Paper>
      </Slide> */}

      <Slide direction="left" in={dividerMode}>
        <Box
          display="flex"
          alignItems="center"
          style={{
            position: 'absolute',
            left: props.left + 35 + 100,
            top: props.top + 48 - 25, //48 is header height
            columnGap: 5,
            zIndex: 20,
          }}
        >
          <TextField
            label="count"
            variant="outlined"
            size="small"
            type="number"
            style={{
              width: 80,
            }}
            value={dividerCount}
            onChange={(e) => {
              const val = parseInt(e.target.value)
              if (val > 0) {
                setDividerCount(val)
                props.viewDividers(val)
              } else {
                props.viewDividers(1)
              }
            }}
          />
          <Fab
            style={{
              background: '#7EE214',
              color: 'white',
              maxWidth: 25,
              maxHeight: 25,
              minWidth: 25,
              minHeight: 25,
            }}
            onClick={() => {
              setDividerMode(false)
              props.addTempDividers()
            }}
          >
            <CheckIcon />
          </Fab>
          <Fab
            style={{
              maxWidth: 25,
              maxHeight: 25,
              minWidth: 25,
              minHeight: 25,
            }}
            color="secondary"
            onClick={() => {
              props.removeTempDividers()
              setDividerMode(false)
            }}
          >
            <CloseIcon />
          </Fab>
        </Box>
      </Slide>

      <Slide direction="left" in={shelfMode}>
        <Box
          display="flex"
          alignItems="center"
          style={{
            position: 'absolute',
            left: props.left + 35 + 100,
            top: props.top + 48 - 25 + 36 + 10, //48 is header height
            columnGap: 5,
            zIndex: 20,
          }}
        >
          <TextField
            label="count"
            variant="outlined"
            size="small"
            type="number"
            style={{
              width: 80,
            }}
            value={shelfCount}
            onChange={(e) => {
              const val = parseInt(e.target.value)
              if (val > 0) {
                setShelfCount(val)
                props.viewShelf(val)
              }
            }}
          />
          <Fab
            style={{
              background: '#7EE214',
              color: 'white',
              maxWidth: 25,
              maxHeight: 25,
              minWidth: 25,
              minHeight: 25,
            }}
            onClick={() => {
              setShelfMode(false)
              props.addShelvesAsItem(shelfCount)
            }}
          >
            <CheckIcon />
          </Fab>

          <Fab
            style={{
              maxWidth: 25,
              maxHeight: 25,
              minWidth: 25,
              minHeight: 25,
            }}
            color="secondary"
            onClick={turnOffShelvesMode}
          >
            <CloseIcon />
          </Fab>

          <Fab
            style={{
              maxWidth: 25,
              maxHeight: 25,
              minWidth: 25,
              minHeight: 25,
            }}
            color="secondary"
            onClick={() => {
              props.deleteShelves()
              setShelfMode(false)
            }}
          >
            <DeleteIcon />
          </Fab>
        </Box>
      </Slide>
    </>
  )
}

export default SectionActions
